import React, { useRef, useCallback, useEffect, useState } from 'react';
import JobCard from '../job-cards/JobCard';
import JobBadges from '../indicators/JobBadges';
import EditButton from '../indicators/EditButton';
import LoadingSpinner from '../utils/LoadingSpinner';
import EmptyState from '../utils/EmptyState';
import JobDateGroup from './JobDateGroup';
import { Link } from 'react-router-dom';

export default function PendingJobsColumn({
  pendingJobsByDate = {},
  jobsLoading = false,
  isLoadingMore = false,
  selectedJobs = new Set(),
  onJobSelect,
  onJobEdit,
  onJobViewDetails,
  canEditJobs = false,
  allJobs = [],
  hasMore = false,
  handleLoadMore = () => {},
  totalJobs = 0,
  displayedJobsCount = 0,
  jobsByDateTotals = {},
  searchTerm = '',
  selectedAreas = [],
  selectedServiceTypes = [],
  selectedCategories = [],
  currentDateRangeDescription = '',
  userHasSelectedDateRange = false
}) {
  // Setup scroll event listener for infinite scrolling
  const jobListRef = useRef(null);
  const isLoadingMoreRef = useRef(false);
  
  // Infinite scroll refs
  const observerRef = useRef(null);
  const loadMoreTriggerRef = useRef(null);
  const lastLoadMoreTimeRef = useRef(0);
  const scrollThrottleRef = useRef(null);
  
  // Check if we have any jobs to display
  const hasJobs = Object.keys(pendingJobsByDate).length > 0;
  
  // Define appropriate loading states
  const isInitialLoad = jobsLoading && !hasJobs;
  const isCurrentlyLoadingMore = (jobsLoading && hasJobs) || isLoadingMore;
  
  // Enhanced loading state
  const isAnyLoading = isInitialLoad || isCurrentlyLoadingMore;
  
  // Throttled load more function to prevent excessive API calls
  const throttledLoadMore = useCallback(() => {
    const now = Date.now();
    const throttleDelay = 1000; // 1 second throttle
    
    if (now - lastLoadMoreTimeRef.current < throttleDelay) {
      return;
    }
    
    if (isLoadingMoreRef.current || !hasMore || isCurrentlyLoadingMore) {
      return;
    }
    
    lastLoadMoreTimeRef.current = now;
    isLoadingMoreRef.current = true;
    

    
    // Call the handler with default limit
    handleLoadMore();
  }, [handleLoadMore, hasMore, isCurrentlyLoadingMore]);

  // Setup Intersection Observer for infinite scroll
  useEffect(() => {
    if (!loadMoreTriggerRef.current || !hasMore || !jobListRef.current) {
      return;
    }

    // Cleanup previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Create new Intersection Observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && hasMore && !isCurrentlyLoadingMore) {
            // Use throttled function to prevent rapid firing
            if (scrollThrottleRef.current) {
              clearTimeout(scrollThrottleRef.current);
            }
            
            scrollThrottleRef.current = setTimeout(() => {
              throttledLoadMore();
            }, 50); // Reduced delay for more responsive scrolling
          }
        });
      },
      {
        root: jobListRef.current, // Use the container as the scroll root
        rootMargin: '50px', // Trigger when element is 50px from bottom of container
        threshold: 0.1
      }
    );

    // Observe the trigger element
    observerRef.current.observe(loadMoreTriggerRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (scrollThrottleRef.current) {
        clearTimeout(scrollThrottleRef.current);
      }
    };
  }, [throttledLoadMore, hasMore, isCurrentlyLoadingMore]);

  // Reset loading flag when loading completes
  useEffect(() => {
    if (!isCurrentlyLoadingMore) {
      isLoadingMoreRef.current = false;
    }
  }, [isCurrentlyLoadingMore]);

  // Determine if we're in search mode
  const isSearching = searchTerm && searchTerm.length >= 2;
  
  // Check if any filters are active
  const hasAreaFilter = selectedAreas && selectedAreas.length > 0;
  const hasServiceTypeFilter = selectedServiceTypes && selectedServiceTypes.length > 0;
  const hasCategoryFilter = selectedCategories && selectedCategories.length > 0;
  const hasAnyFilter = hasAreaFilter || hasServiceTypeFilter || hasCategoryFilter || isSearching;

  // Generate contextual empty state description
  const getEmptyStateDescription = () => {
    if (isInitialLoad) {
      return "Loading jobs...";
    }
    
    if (isSearching) {
      return `No jobs match your search for "${searchTerm}"`;
    }
    
    if (hasAnyFilter) {
      const activeFilters = [];
      if (hasAreaFilter) activeFilters.push(`${selectedAreas.length} area${selectedAreas.length > 1 ? 's' : ''}`);
      if (hasServiceTypeFilter) activeFilters.push(`${selectedServiceTypes.length} service type${selectedServiceTypes.length > 1 ? 's' : ''}`);
      if (hasCategoryFilter) activeFilters.push(`${selectedCategories.length} categor${selectedCategories.length > 1 ? 'ies' : 'y'}`);
      
      const filterText = activeFilters.join(', ');
      return `No jobs found for the selected ${filterText}. Try adjusting your filters or selecting a different date range.`;
    }
    
    return "There are no jobs scheduled for this date range";
  };

  // Render standard action buttons for a job
  const renderJobActions = (job) => (
    <>
      <button
        onClick={() => onJobViewDetails && onJobViewDetails(job.id)}
        className="inline-flex items-center px-3 py-1.5 border border-blue-500 text-xs font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 whitespace-nowrap"
      >
        View Details
        <svg className="ml-1.5 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
        </svg>
      </button>
      
      {canEditJobs && <EditButton onClick={() => onJobEdit(job)} />}
    </>
  );

  // Jobs renderer with memoized card creation
  const renderJobCard = (job, index) => (
    <JobCard
      key={job.id}
      job={job}
      index={index}
      selected={selectedJobs.has(job.id)}
      onSelect={onJobSelect}
      onJobViewDetails={onJobViewDetails}
      renderBadges={(job) => <JobBadges job={job} allJobs={allJobs} />}
      renderActions={(job) => renderJobActions(job)}
      isSchedulePage={true}
    />
  );

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div 
        className="p-2 sm:p-6 job-list-container overflow-y-auto h-[750px]"
        id="job-list-container"
        ref={jobListRef}

      >
        <div className="space-y-4 sm:space-y-8">
          {isInitialLoad ? (
            <LoadingSpinner color="border-sky-600" />
          ) : !hasJobs ? (
            <EmptyState 
              title="No jobs available"
              description={getEmptyStateDescription()}
              icon="calendar"
            />
          ) : (
            Object.entries(pendingJobsByDate).map(([date, jobs]) => (
              <JobDateGroup
                key={date}
                date={date}
                jobs={jobs}
                renderJobCard={renderJobCard}
                badgeColor="bg-blue-100 text-blue-800"
                jobsByDateTotals={jobsByDateTotals}
              />
            ))
          )}
        </div>
        
        {/* Infinite scroll trigger element - only show when there are more jobs to load */}
        {hasMore && hasJobs && (
          <div 
            ref={loadMoreTriggerRef}
            className="py-4 text-center"
          >
            {isCurrentlyLoadingMore ? (
              <div className="inline-flex items-center px-4 py-2 bg-white shadow-sm rounded-md">
                <svg className="animate-spin h-5 w-5 text-blue-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading more jobs...
              </div>
            ) : (
              <div className="text-sm text-gray-500">
                {currentDateRangeDescription ? (
                  <>
                    <div>Scroll to load more jobs</div>
                    <div className="text-xs text-gray-400 mt-1">
                      Currently showing: {currentDateRangeDescription}
                    </div>
                  </>
                ) : (
                  'Scroll to load more jobs'
                )}
              </div>
            )}
          </div>
        )}
        
        {/* Show end message when no more jobs */}
        {!hasMore && hasJobs && (
          <div className="py-4 text-center">
            <div className="text-sm text-gray-500">
              {userHasSelectedDateRange ? (
                <>
                  <div>No more jobs found in selected date range</div>
                  <div className="text-xs text-gray-400 mt-1">
                    Date range: {currentDateRangeDescription}
                  </div>
                </>
              ) : currentDateRangeDescription ? (
                <>
                  <div>You've reached the end of all available jobs</div>
                  <div className="text-xs text-gray-400 mt-1">
                    Searched up to: {currentDateRangeDescription}
                  </div>
                </>
              ) : (
                "You've reached the end of all available jobs"
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 